import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useApi, useToaster } from "@shopexperts/shared";
import type { AdminExpertDetail, UpdateExpertDetailsRequest } from "@shopexperts/legacy-api";

export interface UpdateExpertDetailsVariables {
  expertId: number;
  body: UpdateExpertDetailsRequest;
}

export function useExpertDetailsUpdate() {
  const api = useApi();
  const queryClient = useQueryClient();
  const toaster = useToaster();

  const updateDetails = useMutation({
    mutationFn: async ({ expertId, body }: UpdateExpertDetailsVariables) => {
      return await api.admin.expertManager.updateDetails({
        params: { expertId },
        body,
      });
    },
    onSuccess: (detail: AdminExpertDetail) => {
      // The response is the fresh detail payload; writing it into the cache lets the
      // section flip back to read mode with the saved values instead of the stale ones.
      queryClient.setQueriesData<AdminExpertDetail>(
        {
          predicate: (query) =>
            query.queryKey.length === 3
            && query.queryKey[0] === "admin"
            && query.queryKey[1] === "expert"
            && String(query.queryKey[2]) === String(detail.id),
        },
        () => detail,
      );
      queryClient.invalidateQueries({ queryKey: ["admin", "experts"] });
      toaster.create({ title: "Details saved", type: "success" });
    },
  });

  return { updateDetails };
}
